<template>
    <span>
        <h3 v-if="ifModal" class="title">
            Contact {{ lenderName }}
            <span v-if="loanType">{{ loanTerm }} ({{ loanType }})</span>
            <span v-else>{{ loanTerm }}</span>
        </h3>
        <ul class="list-unstyled contact-form">
            <span v-if="ifModal">
                <li>Obligation free - contact for more information</li>
                <li class="loan-term">{{ loanType }} {{ loanTerm }}</li>
                <li v-if="rate || apr || fees"><b><img :src="offersAttributes[2]" />  Rate / <img :src="offersAttributes[1]" /> APR / {{ feesFormatted }} Fees</b></li>
            </span>
            <li>
                <input
                    v-model.trim="FirstName"
                    type="text"
                    placeholder="First Name"
                    :class="{'error': ($v.FirstName.$invalid && $v.FirstName.$dirty) || !IsFirstNameValid}"
                    @focus="IsFirstNameValid=FirstName.length!=0"
                    @blur="IsFirstNameValid=true,displayError()"
                    @input="fieldValidation('firstName')"
                />
                <span v-if="(!$v.FirstName.required ||!$v.FirstName.minLength) && $v.FirstName.$dirty" class="error-message red-text">Please enter your first name</span>
            </li>
            <li>
                <input
                    v-model.trim="LastName"
                    type="text"
                    placeholder="Last Name"
                    :class="{'error': ($v.LastName.$invalid && $v.LastName.$dirty) || !IsLastNameValid }"
                    @focus="IsLastNameValid=LastName.length!=0"
                    @blur="IsLastNameValid=true,displayError()"
                    @input="fieldValidation('lastName')"
                />
                <span v-if="(!$v.LastName.required ||!$v.LastName.minLength) && $v.LastName.$dirty" class="error-message red-text">Please enter your last name</span>
            </li>
            <li>
                <input
                    v-model.trim="Email"
                    type="text"
                    placeholder="Email"
                    :class="{'error': !IsEmailBlank || !IsEmailValid ||( $v.Email.$invalid && $v.Email.$dirty) }"
                    @focus="IsEmailBlank=Email.length!=0"
                    @blur="IsEmailBlank=true"
                    @input="$v.Email.$touch(), validateEmail(Email)"
                />
                <span v-if="!IsEmailValid ||(!$v.Email.required && $v.Email.$dirty)" class="error-message red-text">Please enter <span v-if="!IsEmailValid">a valid </span>email address</span>
            </li>
            <li>
                <input
                    v-model.trim="Phone"
                    v-mask="'(###) ###-####'"
                    type="tel"
                    :class="{'error': !IsPhoneValid || ($v.Phone.$invalid && $v.Phone.$dirty)}"
                    placeholder="(999) 999-9999"
                    maxlength="14"
                    @input="$v.Phone.$touch(), validatePhone(Phone)"
                />
                <span v-if="!IsPhoneValid ||(!$v.Phone.required && $v.Phone.$dirty)" class="error-message red-text">Please enter a valid phone number</span>
            </li>
            <li :class="{ 'text-center': !contactNumber || !ShowPhoneNumbers }">
                <button class="btn btn-blue" :disabled="IsButtonDisabled" @click="submitDataLead($event)">{{ buttonText }}</button>
                <div class="successSubmit">Success!</div>
                <a v-if="contactNumber && ShowPhoneNumbers && isPhoneIndicator" class="cell-no" :href="'tel:' + contactNumber" @click="contactNoClicked()">or Call <b>{{ contactNumber }}</b></a>
            </li>
        </ul>
        <div v-if="ifModal" class="legalize-text">
            <p>By clicking above, you are providing express written consent for LendingTree, the lender listed above, or parties calling on their behalf to call you (including through automated means; e.g., autodialing, text, and pre-recorded messaging) via telephone or mobile device (including SMS and MMS messaging), even if your telephone number is currently listed on any corporate, state, or federal Do-Not-Call list.</p>
            <p>Consent to being called is not required to utilize these services. You may choose to call a LendingTree customer care representative at <a href="tel:(888)-272-1355">1-888-272-1355</a>.</p>
        </div>
        <div v-if="!ifModal && showCancel" class="text-right">
            <a @click="$emit('closeContactBlock')">Cancel</a>
        </div>

        <div v-if="!ifModal" class="disclaimer">
            By clicking the “Get Started” button above, I agree to LendingTree sending the information provided to  {{ lenderName }} and I authorize {{ lenderName }} to use the phone number provided for telemarketing calls which may come from automated dialing systems, prerecorded message, and/or text messages.  Consent to the above is not required as a condition to utilize LendingTree’s or {{ lenderName }}.
        </div>
    </span>
</template>

<script>
    import Accounting from 'accounting';
    import {
        email, required, minLength,
    } from 'vuelidate/lib/validators';
    import { debounce } from 'lodash-es';
    import briteverify from '../services/briteverify';
    import TrackDataHelper from '../helpers/track-data-helper';
    import AnalyticsTrack from '../mixin/analytics-track';
    import AnalyticsHelper from '../helpers/analytics';
    import submitLeadDetails from '../mixin/preprime-submit-lead-details';

    export default {
        props: {
            loanTerm: {
                default: '',
            },
            loanType: {
                default: '',
            },
            rate: {
                default: 0,
            },
            fees: {
                default: 0,
            },
            contactBtnClass: {
                default: 'btn btn-orange',
            },
            contactBtnText: {
                default: 'Contact',
            },
            ifModal: {
                default: false,
            },
            showCancel: {
                default: false,
            },
            showPhoneNumbers: {
                default: true,
            },
            placement: {
                default: '',
            },
            vertical: {
                default: '',
            },
            isPhoneIndicator: {
                default: true,
            },
            elClicked: {
                default: '',
            },
            context: {
                default: 'www',
            },
        },
        data() {
            return {
                IsPhoneValid: true,
                IsEmailBlank: true,
                IsFirstNameValid: true,
                IsLastNameValid: true,
                IfModal: this.ifModal,
                ShowPhoneNumbers: this.showPhoneNumbers,
                LoanType: this.loanType,
                Vertical: this.vertical,
                Placement: this.placement,
                LeadGuid: null,
                LendingEntityId: this.lendingEntityId,
            };
        },
        validations: {
            FirstName:
                {
                    required,
                    minLength: minLength(2),
                    isValidFirstName(FirstName) {
                        const regex = /^[a-zA-Z-' ]+$/;
                        return regex.test(FirstName);
                    },
                },
            LastName:
                {
                    required,
                    minLength: minLength(2),
                    isValidLastName(LastName) {
                        const regex = /^[a-zA-Z-' ]+$/;
                        return regex.test(LastName);
                    },
                },
            Phone:
                {
                    required,
                    minLength: minLength(14),
                },
            Email:
                {
                    required,
                    email,
                },
        },
        computed: {
            ratePercentage() {
                return Accounting.formatMoney(this.rate, { precision: 3, format: '%v' });
            },
            aprPercentage() {
                return Accounting.formatMoney(this.apr, { precision: 3, format: '%v' });
            },
            feesFormatted() {
                return Accounting.formatMoney(this.fees, { precision: 0 });
            },
            buttonText() {
                return this.IfModal ? 'Submit' : 'Get Started';
            },
        },
        methods: {
            submitDataLead(event) {
                const success = document.querySelector('.successSubmit');
                const parentEl = this.elClicked;
                // UI confirmation
                if (success && success.style) success.style.display = 'block';
                event.target.classList.add('green-circle-popup');
                event.target.innerHTML = '<div class="lt4-Check"></div>';

                parentEl.nextElementSibling.style.display = 'block';
                parentEl.classList.add('green-circle');
                parentEl.innerHTML = '<div class="lt4-Check"></div>';

                this.submitPrePrimeReferel();
                setTimeout(this.clearModalData, 1000);
            },
            contactNoClicked() {
                TrackDataHelper.setMany(this.trackContactLenderClicked(this.apr, this.lenderName, this.offerId, this.loanAmount, this.contactNumber, this.loanPurpose()), 'contactform');
                AnalyticsHelper.track('LE Phone No Clicked', TrackDataHelper.get('contactform'));
            },
            clearModalData() {
                this.FirstName = '';
                this.LastName = '';
                this.Email = '';
                this.Phone = '';
                this.IsPhoneValid = true;
                this.IsEmailValid = true;
                this.IsButtonDisabled = true;
                this.$v.$reset();
                this.$emit('closeModal');
            },
            doVarificationCallback(type, getStatus) {
                if (type === 'phone') this.IsPhoneValid = getStatus;
                else this.IsEmailValid = getStatus;
                this.fieldValidation('brite');
            },
            validateEmail(value) {
                this.debounceEmailValidation(value, this);
            },
            validatePhone(value) {
                this.debouncePhoneValidation(value, this);
            },
            debounceEmailValidation: debounce((value, self) => {
                if (self.$v.Email.$invalid && self.$v.Email.required) self.IsEmailValid = false;
                else briteverify.doVarification(value, 'email', self.doVarificationCallback);
            }, 500),
            debouncePhoneValidation: debounce((value, self) => {
                if (self.$v.Phone.$invalid) {
                    self.IsPhoneValid = false;
                } else {
                    briteverify.doVarification(value, 'phone', self.doVarificationCallback);
                }
            }, 500),
            fieldValidation(type) {
                if (type === 'firstName') {
                    this.IsFirstNameValid = this.FirstName.length >= 2;
                    if (this.FirstName.length === 0 || this.IsFirstNameValid) {
                        this.$v.FirstName.$touch();
                    } else {
                        this.$v.FirstName.$reset();
                    }
                }
                if (type === 'lastName') {
                    this.IsLastNameValid = this.LastName.length >= 2;
                    if (this.LastName.length === 0 || this.IsLastNameValid) {
                        this.$v.LastName.$touch();
                    } else {
                        this.$v.LastName.$reset();
                    }
                }
                this.IsEmailBlank = true;
                this.IsButtonDisabled = !(!this.$v.$error && this.FirstName.length >= 2 && this.LastName.length >= 2 && (this.IsEmailValid && this.Email.length !== 0) && (this.IsPhoneValid && this.Phone.length !== 0));
            },
            displayError() {
                if (this.FirstName.length > 0) this.$v.FirstName.$touch();
                if (this.LastName.length > 0) this.$v.LastName.$touch();
            },
        },
        mixins: [AnalyticsTrack, submitLeadDetails],
    };
</script>
<style lang="scss">
@import "../../assets/styles/variables";

#lt-yantr {
    .lender-offers {
        .contact-form {
            & > span {
                > li {
                    img {
                        min-height: 30px;
                        margin-top: -3px;
                        max-height: 30px;
                    }
                }
            }
        }
    }
}
</style>
